/**
 * Connected Accounts - Manage social media account connections
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  Badge,
  Button,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Input,
  FormControl,
  FormLabel,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { collection, doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { getCurrentDb } from '../../config/firebaseManager';
import { format } from 'date-fns';

interface SocialMediaAccount {
  platform: string;
  accountName: string;
  accountId: string;
  accessToken: string;
  tokenExpiresAt: Timestamp;
  isActive: boolean;
}

interface PlatformConfig {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
}

const PLATFORMS: PlatformConfig[] = [
  {
    id: 'facebook',
    name: 'Facebook',
    icon: '📘',
    color: 'blue',
    description: 'Post to your Facebook Page',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: '📷',
    color: 'pink',
    description: 'Share photos and stories',
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: '💼',
    color: 'linkedin',
    description: 'Professional networking',
  },
];

export default function ConnectedAccounts() {
  const [accounts, setAccounts] = useState<Record<string, SocialMediaAccount | null>>({});
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Form state
  const [accountName, setAccountName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('60');

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const db = getCurrentDb();
      if (!db) return;

      const accountsData: Record<string, SocialMediaAccount | null> = {};

      for (const platform of PLATFORMS) {
        const docRef = doc(db, 'socialMediaAccounts', platform.id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          accountsData[platform.id] = docSnap.data() as SocialMediaAccount;
        } else {
          accountsData[platform.id] = null;
        }
      }

      setAccounts(accountsData);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast({
        title: 'Error loading accounts',
        description: 'Failed to fetch connected accounts.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = (platformId: string) => {
    setSelectedPlatform(platformId);
    const account = accounts[platformId];
    
    if (account) {
      // Pre-fill form with existing data
      setAccountName(account.accountName);
      setAccountId(account.accountId);
      setAccessToken(account.accessToken);
      
      // Calculate days until expiration
      const now = new Date();
      const expiresAt = account.tokenExpiresAt.toDate();
      const daysUntilExpiry = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      setExpiresInDays(Math.max(0, daysUntilExpiry).toString());
    } else {
      // Clear form for new connection
      setAccountName('');
      setAccountId('');
      setAccessToken('');
      setExpiresInDays('60');
    }
    
    onOpen();
  };

  const handleSave = async () => {
    if (!selectedPlatform) return;

    try {
      const db = getCurrentDb();
      if (!db) return;

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + parseInt(expiresInDays));

      const accountData: SocialMediaAccount = {
        platform: selectedPlatform,
        accountName,
        accountId,
        accessToken,
        tokenExpiresAt: Timestamp.fromDate(expiresAt),
        isActive: true,
      };

      const docRef = doc(db, 'socialMediaAccounts', selectedPlatform);
      await setDoc(docRef, accountData);

      toast({
        title: '✅ Account connected!',
        description: `${PLATFORMS.find(p => p.id === selectedPlatform)?.name} is now connected.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onClose();
      fetchAccounts();
    } catch (error) {
      console.error('Error saving account:', error);
      toast({
        title: 'Error',
        description: 'Failed to save account connection.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDisconnect = async (platformId: string) => {
    try {
      const db = getCurrentDb();
      if (!db) return;

      const docRef = doc(db, 'socialMediaAccounts', platformId);
      await updateDoc(docRef, { isActive: false });

      toast({
        title: 'Account disconnected',
        description: `${PLATFORMS.find(p => p.id === platformId)?.name} has been disconnected.`,
        status: 'info',
        duration: 5000,
        isClosable: true,
      });

      fetchAccounts();
    } catch (error) {
      console.error('Error disconnecting account:', error);
      toast({
        title: 'Error',
        description: 'Failed to disconnect account.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const isTokenExpired = (account: SocialMediaAccount | null): boolean => {
    if (!account) return false;
    const now = new Date();
    const expiresAt = account.tokenExpiresAt.toDate();
    return expiresAt < now;
  };

  const getDaysUntilExpiry = (account: SocialMediaAccount | null): number => {
    if (!account) return 0;
    const now = new Date();
    const expiresAt = account.tokenExpiresAt.toDate();
    return Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" />
        <Text mt={4}>Loading connected accounts...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="lg" mb={2}>Connected Accounts</Heading>
      <Text color="gray.600" mb={6}>
        Manage your social media account connections for publishing.
      </Text>

      <Alert status="info" mb={6}>
        <AlertIcon />
        <Box>
          <AlertTitle>Manual Setup Required</AlertTitle>
          <AlertDescription>
            For now, you'll need to manually add access tokens. See the deployment guide for instructions
            on getting tokens from Facebook, Instagram, and LinkedIn.
          </AlertDescription>
        </Box>
      </Alert>

      <VStack spacing={4} align="stretch">
        {PLATFORMS.map((platform) => {
          const account = accounts[platform.id];
          const isConnected = account && account.isActive;
          const expired = isTokenExpired(account);
          const daysLeft = getDaysUntilExpiry(account);

          return (
            <Card key={platform.id}>
              <CardBody>
                <HStack justify="space-between" align="start">
                  <HStack spacing={4} flex={1}>
                    <Text fontSize="3xl">{platform.icon}</Text>
                    <Box>
                      <Heading size="md">{platform.name}</Heading>
                      <Text fontSize="sm" color="gray.600" mb={2}>
                        {platform.description}
                      </Text>
                      
                      {isConnected && (
                        <VStack align="start" spacing={1}>
                          <HStack>
                            <Badge colorScheme={expired ? 'red' : 'green'}>
                              {expired ? '❌ Expired' : '✅ Connected'}
                            </Badge>
                            {!expired && daysLeft <= 7 && (
                              <Badge colorScheme="orange">
                                ⚠️ Expires in {daysLeft} days
                              </Badge>
                            )}
                          </HStack>
                          <Text fontSize="xs" color="gray.500">
                            Account: {account.accountName}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            ID: {account.accountId}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            Expires: {format(account.tokenExpiresAt.toDate(), 'MMM dd, yyyy')}
                          </Text>
                        </VStack>
                      )}
                      
                      {!isConnected && (
                        <Badge colorScheme="gray">Not Connected</Badge>
                      )}
                    </Box>
                  </HStack>

                  <VStack spacing={2}>
                    {isConnected ? (
                      <>
                        <Button
                          size="sm"
                          colorScheme={platform.color}
                          onClick={() => handleConnect(platform.id)}
                        >
                          {expired ? '🔄 Reconnect' : '⚙️ Update'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDisconnect(platform.id)}
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        colorScheme={platform.color}
                        onClick={() => handleConnect(platform.id)}
                      >
                        Connect
                      </Button>
                    )}
                  </VStack>
                </HStack>
              </CardBody>
            </Card>
          );
        })}
      </VStack>

      {/* Connection Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedPlatform && PLATFORMS.find(p => p.id === selectedPlatform)?.icon}{' '}
            Connect {selectedPlatform && PLATFORMS.find(p => p.id === selectedPlatform)?.name}
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="warning" fontSize="sm">
                <AlertIcon />
                <Box>
                  <AlertTitle fontSize="sm">Manual Token Entry</AlertTitle>
                  <AlertDescription fontSize="xs">
                    Get your access token from the developer console. See the deployment guide for detailed instructions.
                  </AlertDescription>
                </Box>
              </Alert>

              <FormControl isRequired>
                <FormLabel>Account Name</FormLabel>
                <Input
                  placeholder="e.g., Glisten Eco-Shine"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Account ID</FormLabel>
                <Input
                  placeholder={
                    selectedPlatform === 'facebook' ? 'Facebook Page ID' :
                    selectedPlatform === 'instagram' ? 'Instagram Business Account ID' :
                    'LinkedIn Person ID'
                  }
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Access Token</FormLabel>
                <Textarea
                  placeholder="Paste your access token here"
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  rows={4}
                  fontFamily="monospace"
                  fontSize="sm"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Token Expires In (Days)</FormLabel>
                <Input
                  type="number"
                  placeholder="60"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Most tokens expire in 60 days. Check your platform's documentation.
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isDisabled={!accountName || !accountId || !accessToken}
            >
              Save Connection
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
